<i18n>
{
  "fr": {
    "configuratorLayerEditorStage": {
      "defaultText": "Votre texte ici"
    }
  }
}
</i18n>

<template>
  <div ref="container" class="configurator-layer-editor-stage">
    <v-stage ref="stage" :config="stageConfig">
      <v-layer
        v-if="promiseHelper.isFulfilled(PromiseKey.STAGE_DIMENSIONS_READY)"
      >
        <v-group :config="containerGroupConfig">
          <v-image ref="shapeImage" :config="shapeImageConfig" />

          <v-group :config="overlappingGroupConfig">
            <ConfiguratorLayerEditorStageZoneFactory
              v-if="transformableShapeReady"
              v-bind="activeShapeOverlappingImageConfig"
            />
          </v-group>

          <v-group :config="printingAreaGroupConfig">
            <v-rect :config="printingAreaRectConfig" />

            <v-group
              v-for="zoneConfig in readOnlyZoneConfig"
              :key="zoneConfig.id"
              :config="zoneConfig.groupConfig"
            >
              <!-- Debug rect -->
              <!-- <v-rect-->
              <!--   :config="{-->
              <!--     ...zoneConfig.groupConfig,-->
              <!--     x: 0,-->
              <!--     y: 0,-->
              <!--     fill: 'rgba(255,0,0,0.05)',-->
              <!--   }"-->
              <!-- />-->

              <ConfiguratorLayerEditorStageZoneFactory
                v-bind="zoneConfig.shapeConfig"
              />
            </v-group>

            <v-group :config="zoneGroupConfig">
              <ConfiguratorLayerEditorStageZoneFactory
                v-if="transformableShapeReady"
                ref="activeShape"
                v-bind="activeShapeConfig"
              />

              <!-- Keep zone rect on top of its content -->
              <v-rect :config="zoneRectConfig" />
            </v-group>
          </v-group>
        </v-group>

        <v-group :config="containerGroupConfig">
          <!-- Using a fake shape with transformer because transformer handles are hidden when outside of group clipping bounds. -->
          <ConfiguratorLayerEditorStageZoneFactory
            v-if="transformableShapeReady"
            :id="transformableShapeId"
            ref="transformableShape"
            v-bind="transformableShapeProps"
            @transform="onTransform"
            @transformend="onTransformEnd"
            @dragend="onDragEnd"
            @dragmove="onDrag"
            @touchmouve="onDrag"
            @click="$emit('focus-request')"
            @touchend="
              e => {
                if (!isDragging) {
                  e.evt.preventDefault();
                  $emit('focus-request');
                }
              }
            "
            @mouseenter="onTransformableShapeMouseEnter"
            @mouseleave="onTransformableShapeMouseLeave"
          />

          <v-transformer ref="transformer" :config="transformerConfig" />
        </v-group>
      </v-layer>
    </v-stage>
  </div>
</template>

<script>
import { ResizeObserver as ResizeObserverPolyfill } from '@juggle/resize-observer/lib/ResizeObserver';
import {
  fitArea,
  ZoneCustomizationCustomConfigProps,
} from '../../utils/StageUtils';
import { loadImageAndCache } from '../../utils/ImageUtils';
import { hex2rgba } from '../../utils/ColorUtils';
import { RGB } from 'konva/lib/filters/RGB';
import { Invert } from 'konva/lib/filters/Invert';
import { filterProps, transformProps } from '../../utils/ObjectUtils';
import { randomStr } from '../../utils/String';
import ConfiguratorLayerEditorStageZoneFactory from './ConfiguratorLayerEditorStage/ConfiguratorLayerEditorStageZoneFactory';
import { ZoneType } from '../../api/static/zones';
import PromiseHelper from '../../utils/PromiseHelper';
import { mmToPx, pxToMm, Resolution } from '../../utils/MathUtils';
import { getTypoName } from '../../utils/TypoUtils';
import {
  GlassesGetter,
  prefixType as prefixGlassesType,
} from '../../store/modules/configurator/glasses';

const ResizeObserver = globalThis.ResizeObserver || ResizeObserverPolyfill;

const STAGE_PADDING_PX = 16;
const MAX_ARC_ANGLE = 120;
const HINT_COLOR = '#67a9ff';

const PromiseKey = Object.freeze({
  STAGE_DIMENSIONS_READY: 'stageDimensionsReady',
});

export default {
  name: 'ConfiguratorLayerEditorStage',
  emits: ['focus-request'],
  components: { ConfiguratorLayerEditorStageZoneFactory },
  model: {
    prop: 'shapeConfig',
    event: 'change',
  },
  props: {
    shapeWidth: { type: Number, required: true },
    shapeHeight: { type: Number, required: true },
    shapeUrl: { type: String, required: true },
    printingArea: {
      type: Object,
      required: true,
      validator(v) {
        return ['x', 'y', 'width', 'height'].every(prop => {
          return prop in v && typeof v[prop] === 'number';
        });
      },
    },
    imageUrl: { type: String, default: undefined },
    readOnlyCustomizations: { type: Array, default: () => [] },
    activeCustomization: { type: Object, required: true },
    group: { type: Object, required: true },
    /**
     * @type {ZoneCustomizationCustomConfig}
     */
    shapeConfig: {
      type: Object,
      default: undefined,
      validator(obj) {
        return ZoneCustomizationCustomConfigProps.every(prop => prop in obj);
      },
    },
    text: {
      type: String,
      default: undefined,
    },
    bold: Boolean,
    underlined: Boolean,
    italic: Boolean,
    fontFamily: {
      type: String,
      default: undefined,
    },
    screenPrintingColor: {
      type: String,
      default: undefined,
    },
    color: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      resizeObserver: null,
      canvasDimensions: {
        width: null,
        height: null,
      },
      shapeImage: null,
      transformableShapeId: `transformable-shape-id--${randomStr()}`,
      activeShapeReady: false,
      transformableShapeSharableProps: undefined,
      transformableShapeReady: false,
      promiseHelper: new PromiseHelper(),
      PromiseKey,
      isDragging: false,
    };
  },
  computed: {
    /**
     * @return {{width: number|null, height: number|null}}
     */
    stageConfig() {
      return {
        width: this.canvasDimensions.width,
        height: this.canvasDimensions.height,
      };
    },
    activeZone() {
      return this.group.zoneMap[this.activeCustomization.templateZoneId];
    },
    isZoneProjectionLargerThanDiameter() {
      // Using local variable for better understanding
      const diameter = this.shapeWidth;

      return this.activeZone.width >= (diameter * Math.PI) / 2;
    },
    scaleRatio() {
      const maxWidth = this.isZoneProjectionLargerThanDiameter
        ? this.activeZone.width
        : this.shapeWidth;

      const res = fitArea(
        {
          width: maxWidth,
          height: this.printingArea.height + this.printingArea.y * 2,
        },
        {
          width: this.stageConfig.width - STAGE_PADDING_PX * 2,
          height: this.stageConfig.height - STAGE_PADDING_PX * 2,
        },
        true
      );

      return res.width / maxWidth;
    },
    /**
     * Assuming we use a max width (= a max angle as we represent the cup as a cylinder) to preserve visual aspect.
     * Width above the limit (MAX_ARC_ANGLE) are handled proportionally
     *
     * @return {number}
     */
    projectionScaleRatio() {
      let arcWidth = this.activeZone.width;
      // Formula: angleDeg = (arcWidth × 360) / (π × diameter)
      let arcAngle =
        (this.activeZone.width * 360) / (Math.PI * this.shapeWidth);

      if (arcAngle > MAX_ARC_ANGLE) {
        arcAngle = MAX_ARC_ANGLE;
        arcWidth = (Math.PI * this.shapeWidth * MAX_ARC_ANGLE) / 360;
      }

      // Convert deg to rad: α = (angleDeg × π) / 180
      // Formula: chord = 2r × sin(⍺/2) = diameter × sin(⍺/2)
      const chordWidth =
        this.shapeWidth * Math.sin((arcAngle * Math.PI) / 180 / 2);

      return chordWidth / arcWidth;
    },
    shapeImageConfig() {
      const width = this.shapeWidth * this.scaleRatio;

      const config = {
        x:
          STAGE_PADDING_PX +
          (this.stageConfig.width - 2 * STAGE_PADDING_PX - width) / 2,
        y: STAGE_PADDING_PX,
        width: width,
        height: this.shapeHeight * this.scaleRatio,
        filters: [Invert, RGB],
      };

      if (this.shapeImage) {
        config.image = this.shapeImage;
      }

      return config;
    },
    zoneGroupConfig() {
      return {
        x: this.scaledActiveZone.x,
        y: this.scaledActiveZone.y,
        width: this.scaledActiveZone.width,
        height: this.scaledActiveZone.height,
      };
    },
    zoneRectConfig() {
      return {
        x: 0,
        y: 0,
        width: this.zoneGroupConfig.width,
        height: this.zoneGroupConfig.height,
        stroke: '#df332c',
        strokeWidth: 1,
        dash: [5],
        dashOffset: 0,
      };
    },
    printingAreaSize() {
      return {
        width: this.scaledPrintingArea.width,
        height: this.scaledPrintingArea.height,
      };
    },
    fullScaleRatio() {
      return this.isZoneProjectionLargerThanDiameter
        ? this.scaleRatio
        : this.projectionScaleRatio * this.scaleRatio;
    },
    /**
     * @return {Zone}
     */
    scaledActiveZone() {
      return this.scaleObjProps(this.activeZone);
    },
    /**
     * @return {Object}
     */
    scaledPrintingArea() {
      return this.scaleObjProps(this.printingArea);
    },
    /**
     * @return {ZoneCustomizationCustomConfigImage|ZoneCustomizationCustomConfigText|undefined}
     */
    scaledShapeConfig() {
      return this.scaleShapeConfig(this.shapeConfig);
    },
    activeZoneY() {
      return (
        STAGE_PADDING_PX + this.scaledActiveZone.y + this.scaledPrintingArea.y
      );
    },
    rectAbsolutePosition() {
      return {
        x:
          STAGE_PADDING_PX +
          (this.stageConfig.width -
            2 * STAGE_PADDING_PX -
            this.scaledActiveZone.width) /
            2,
        y: this.activeZoneY,
      };
    },
    printingAreaGroupConfig() {
      const config = {
        x: this.rectAbsolutePosition.x - this.scaledActiveZone.x,
        y: this.rectAbsolutePosition.y - this.activeZone.y,
        ...this.printingAreaSize,
      };

      config.clip = {
        x: 0,
        y: 0,
        width: config.width,
        height: config.height,
      };

      return config;
    },
    printingAreaRectConfig() {
      return {
        x: 0,
        y: 0,
        ...this.printingAreaSize,
        fill: 'rgba(255, 255, 255, 0.75)',
      };
    },
    containerGroupConfig() {
      const height = Math.max(
        this.printingAreaGroupConfig.y + this.printingAreaGroupConfig.height,
        this.printingAreaGroupConfig.y +
          this.zoneRectConfig.y +
          this.zoneRectConfig.height,
        this.shapeImageConfig.y + this.shapeImageConfig.height
      );

      return {
        x: 0,
        // Center vertically whenever possible
        y:
          this.stageConfig.height > height
            ? (this.stageConfig.height - height) / 2
            : 0,
      };
    },
    transformerConfig() {
      return {
        rotateEnabled: false,
        // rotationSnaps: [0, 45, 90, 135, 180, 225, 270, 315],
        enabledAnchors: [
          'top-left',
          'top-right',
          'bottom-left',
          'bottom-right',
        ],
        padding: 10,
        keepRatio: true,
        borderStroke: HINT_COLOR,
        anchorStroke: HINT_COLOR,
      };
    },
    transformableShapeProps() {
      return {
        ...this.transformableShapeSharableProps,
        draggable: true,
        opacity: 0,
      };
    },
    activeShapeConfig() {
      const transformableShapeSharableProps =
        this.transformableShapeSharableProps;

      return {
        ...transformableShapeSharableProps,
        x: transformableShapeSharableProps.x - this.rectAbsolutePosition.x,
        y: transformableShapeSharableProps.y - this.rectAbsolutePosition.y,
      };
    },
    activeShapeOverlappingImageConfig() {
      return {
        ...this.transformableShapeSharableProps,
        opacity: 0.25,
      };
    },
    overlappingGroupConfig() {
      const self = this;

      return {
        x: 0,
        y: 0,
        width: this.stageConfig.width,
        height: this.stageConfig.height,
        clipFunc(ctx) {
          // top
          ctx.rect(
            0,
            0,
            self.stageConfig.width,
            self.printingAreaGroupConfig.y
          );

          const y =
            self.printingAreaGroupConfig.y +
            self.printingAreaGroupConfig.height;

          // Bottom
          ctx.rect(0, y, self.stageConfig.width, self.stageConfig.height - y);

          // left
          if (self.printingAreaGroupConfig.x > 0) {
            ctx.rect(
              0,
              self.printingAreaGroupConfig.y,
              self.printingAreaGroupConfig.x,
              self.printingAreaGroupConfig.height
            );
          }

          const x =
            self.printingAreaGroupConfig.x + self.printingAreaGroupConfig.width;

          // right
          if (self.stageConfig.width > x) {
            ctx.rect(
              x,
              self.printingAreaGroupConfig.y,
              self.stageConfig.width - x,
              self.printingAreaGroupConfig.height
            );
          }
        },
      };
    },
    readOnlyZoneConfig() {
      return this.readOnlyCustomizations.reduce(
        /**
         * @param acc
         * @param {ZoneCustomizationText|ZoneCustomizationCustomConfigImage|ZoneCustomizationCustomConfigText} customization
         */
        (acc, customization) => {
          const { type, text, url, templateZoneId } = customization;

          if (
            (type === ZoneType.TEXT && text) ||
            (type === ZoneType.IMAGE && url)
          ) {
            const { typoId, bold } = customization;

            const typo =
              (typoId &&
                this.$store.getters[
                  prefixGlassesType(GlassesGetter.FIND_TYPO_BY_ID)
                ](typoId)) ||
              undefined;

            const scaledCustomization = this.scaleShapeConfig(customization);

            if (typo) {
              scaledCustomization.fontFamily = getTypoName(typo, bold);
            }

            const existingConfig = {};

            // Replace color with screenPrintingColor (if any)
            if (this.screenPrintingColor) {
              existingConfig.fill = this.screenPrintingColor;
            }

            const shapeConfig = this.zoneToShapeProps(
              customization,
              scaledCustomization,
              existingConfig
            );

            const zone = this.group.zoneMap[templateZoneId];

            acc.push({
              id: customization.id,
              groupConfig: this.scaleObjProps(
                filterProps(zone, ['x', 'y', 'width', 'height'])
              ),
              shapeConfig,
            });
          }

          return acc;
        },
        []
      );
    },
  },
  watch: {
    shapeUrl: {
      async handler(url) {
        this.shapeImage = await loadImageAndCache(url);
      },
      immediate: true,
    },
    async shapeImage() {
      await this.promiseHelper.get(PromiseKey.STAGE_DIMENSIONS_READY);
      await this.renderShapeImage();
    },
    async canvasDimensions() {
      if (this.shapeImage) {
        await this.renderShapeImage();
      }
    },
    activeZone: {
      handler(activeZone) {
        if (activeZone.type === ZoneType.IMAGE && !this.imageUrl) {
          throw new Error('You must provide an image url for image zones.');
        }
      },
      immediate: true,
    },
  },
  created() {
    this.promiseHelper.request(PromiseKey.STAGE_DIMENSIONS_READY);
  },
  mounted() {
    this.initResizeObserver();

    this.promiseHelper.get(PromiseKey.STAGE_DIMENSIONS_READY).then(() => {
      this.$watch(
        'scaledShapeConfig',
        /**
         * @param {{x: number, y: number, scaleX: number, scaleY: number, rotation: number}|undefined} scaledShapeConfig
         */
        async function (scaledShapeConfig) {
          const activeZone = this.activeZone;

          this.transformableShapeSharableProps = this.zoneToShapeProps(
            activeZone,
            {
              ...scaledShapeConfig,
              // Convert zone relative position to absolute position
              x:
                ((scaledShapeConfig && scaledShapeConfig.x) || 0) +
                this.rectAbsolutePosition.x,
              y:
                ((scaledShapeConfig && scaledShapeConfig.y) || 0) +
                this.rectAbsolutePosition.y,
              color: this.color,
              text:
                this.text ||
                this.$t('configuratorLayerEditorStage.defaultText'),
              url: this.imageUrl,
              fontFamily: this.fontFamily,
              fontSize: this.fontSize,
              underlined: this.underlined,
              italic: this.italic,
            }
          );

          this.transformableShapeReady = true;

          await this.$nextTick();

          // Wait for font familly/image to be loaded
          const image = await this.$refs.transformableShape.ready();

          // Resize and center shape if there is no existing position
          if (
            scaledShapeConfig === undefined ||
            scaledShapeConfig.x === undefined ||
            scaledShapeConfig.y === undefined ||
            scaledShapeConfig.scaleX === undefined ||
            scaledShapeConfig.scaleY === undefined
          ) {
            let imageWidth;
            let scaledWidth;
            let scaledHeight;
            let ratio;

            if (activeZone.type === ZoneType.IMAGE) {
              ({ width: imageWidth } = image);
              ({ width: scaledWidth, height: scaledHeight } = fitArea(
                image,
                this.zoneRectConfig
              ));

              ratio = scaledWidth / imageWidth;
            } else if (activeZone.type === ZoneType.TEXT) {
              const textNode = this.$refs.transformableShape.getNode();

              const width = textNode.width();
              const height = textNode.height();

              this.transformableShapeSharableProps.fontSize =
                textNode.fontSize();

              ({ width: scaledWidth, height: scaledHeight } = fitArea(
                { width, height },
                this.zoneRectConfig
              ));

              ratio = scaledWidth / width;
            } else {
              throw new Error('Unknown Zone type.');
            }

            Object.assign(this.transformableShapeSharableProps, {
              x:
                this.rectAbsolutePosition.x +
                (this.zoneRectConfig.width - scaledWidth) / 2,
              y:
                this.rectAbsolutePosition.y +
                (this.zoneRectConfig.height - scaledHeight) / 2,
              scaleX: ratio,
              scaleY: ratio,
            });

            // Emit zone once
            this.updateActiveZone();
          }

          await this.$nextTick();

          // Force render after image/font is loaded
          if (this.$refs.stage) {
            this.$refs.stage.getStage().batchDraw();
          }

          this.updateTransformer();
        },
        { immediate: true }
      );
    });
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },
  methods: {
    /**
     *
     * @param {string} type
     * @param {number} x
     * @param {number} y
     * @param {number} scaleX
     * @param {number} scaleY
     * @param {number} rotation
     * @param {string} text
     * @param {string} url
     * @param {number} fontSize
     * @param {boolean} bold
     * @param {boolean} italic
     * @param {string} fontFamily
     * @param {boolean} underlined
     * @param {string} color
     * @param {object} existingConfig
     * @return {{scaleX: number, scaleY: number, rotation: number, x: number, y: number, type: ZoneType, fill: string|undefined}}
     */
    zoneToShapeProps(
      { type },
      {
        x = 0,
        y = 0,
        scaleX = 1,
        scaleY = 1,
        rotation = 0,
        text,
        url,
        fontSize,
        bold,
        italic,
        fontFamily,
        underlined,
        color,
      } = {},
      existingConfig = {}
    ) {
      const outputConfig = {
        type,
        x,
        y,
        scaleX,
        scaleY,
        rotation,
        fill: color,
        ...existingConfig,
      };

      if (type === ZoneType.TEXT) {
        Object.assign(outputConfig, {
          text,
          fontSize,
          bold,
          italic,
          fontFamily,
          underlined,
        });
      } else if (type === ZoneType.IMAGE) {
        outputConfig.url = url;
      } else {
        throw new Error('Unknown zone type');
      }

      return outputConfig;
    },
    initResizeObserver() {
      this.resizeObserver = new ResizeObserver(entries => {
        entries.forEach(({ contentRect: { width, height } }) => {
          this.canvasDimensions = { width, height };

          if (this.promiseHelper.isPending(PromiseKey.STAGE_DIMENSIONS_READY)) {
            this.promiseHelper.resolve(PromiseKey.STAGE_DIMENSIONS_READY);
          }
        });
      });

      this.resizeObserver.observe(this.$refs.container);
    },
    async renderShapeImage() {
      await this.applyShapeFilter('#aaa');
    },
    async applyShapeFilter(hexColor) {
      await this.$nextTick();

      const imageNode = this.$refs.shapeImage.getNode();

      // See https://stackoverflow.com/a/62836435/1224564
      imageNode.cache({ pixelRatio: 1 });

      const [red, green, blue] = hex2rgba(hexColor);

      imageNode.red(red);
      imageNode.green(green);
      imageNode.blue(blue);

      imageNode.getLayer().batchDraw();
    },
    updateTransformer() {
      const transformer = this.$refs.transformer;

      // TODO Find why transformer is undefined (maybe because modal is closing)
      if (!transformer) {
        return;
      }

      // Attach/Detach transformer node
      const transformerNode = transformer.getStage();

      const stage = transformerNode.getStage();
      const selectedNode =
        stage.findOne(`#${this.transformableShapeId}`) || null;

      // Do nothing if the selected node is already attached
      if (selectedNode === transformerNode.node()) {
        return;
      }

      if (selectedNode) {
        // Attach node to transformer
        transformerNode.nodes([selectedNode]);
      }

      transformerNode.getLayer().batchDraw();
    },
    onTransformEnd(e) {
      this.updateActiveNode(e.target);

      this.updateActiveZone();
    },
    onDragEnd(e) {
      this.isDragging = false;
      this.updateActiveNode(e.target);

      this.updateActiveZone();
    },
    onTransform(e) {
      this.updateActiveNode(e.target);
    },
    onDrag(e) {
      this.isDragging = true;
      this.updateActiveNode(e.target);
    },
    /**
     *
     * @param {Node|Text} node
     */
    updateActiveNode(node) {
      const config = {
        x: node.x(),
        y: node.y(),
        scaleX: node.scaleX(),
        scaleY: node.scaleY(),
        rotation: node.rotation(),
      };

      if (this.activeZone.type === ZoneType.TEXT) {
        config.fontSize = node.fontSize();
      } else if (this.activeZone.type === ZoneType.IMAGE) {
        Object.assign(config, {
          width: node.width(),
          height: node.height(),
        });
      } else {
        throw new Error('Unknown zone type');
      }

      Object.assign(this.transformableShapeSharableProps, config);
    },
    onTransformableShapeMouseEnter() {
      this.$refs.stage.getStage().container().style.cursor = 'move';
    },
    onTransformableShapeMouseLeave() {
      this.$refs.stage.getStage().container().style.cursor = 'default';
    },
    async updateActiveZone() {
      /** @type {ZoneCustomizationCustomConfig} */
      const exportableProps = {
        ...filterProps(this.transformableShapeSharableProps, [
          ...ZoneCustomizationCustomConfigProps,
          'fontSize',
        ]),
      };

      if (this.activeZone && this.activeZone.type === ZoneType.IMAGE) {
        const { width, height } = await this.$refs.transformableShape.ready();

        Object.assign(exportableProps, { width, height });
      }

      // Convert absolute [x, y] to relative
      exportableProps.x -= this.rectAbsolutePosition.x;
      exportableProps.y -= this.rectAbsolutePosition.y;

      // Convert px values to mm
      const unscaledProps = transformProps(
        exportableProps,
        ['x', 'y', 'width', 'height', 'fontSize'],
        v => v / this.fullScaleRatio
      );

      if (unscaledProps.fontSize) {
        // Transform fontSize one more time to px@72dpi
        // (standard theme text font-size is defined that way in back office)
        unscaledProps.fontSize = mmToPx(unscaledProps.fontSize, Resolution.WEB);
      }

      this.$emit('change', unscaledProps);
    },
    scaleObjProps(zone, propList = ['x', 'y', 'width', 'height']) {
      return transformProps(zone, propList, v => v * this.fullScaleRatio);
    },
    /**
     *
     * @param configToScale
     * @return {ZoneCustomizationCustomConfigImage|ZoneCustomizationCustomConfigText|undefined}
     */
    scaleShapeConfig(configToScale) {
      if (configToScale) {
        const shapeConfig = { ...configToScale };

        if (shapeConfig.fontSize) {
          // First, convert fontSize in px@72dpi to mm, before converting it to scaled px
          // (standard theme text font-size is defined that way in back office)
          shapeConfig.fontSize = pxToMm(shapeConfig.fontSize, Resolution.WEB);
        }

        // Convert mm to px (scaled)
        return transformProps(
          shapeConfig,
          ['x', 'y', 'width', 'height', 'fontSize'],
          v => v * this.fullScaleRatio
        );
      }

      return undefined;
    },
  },
};
</script>
